import isURL from "validator/lib/isURL";

/**
 * Whitelist of common valid TLDs (2-3 characters)
 * This ensures we only accept known valid TLDs, not arbitrary short strings
 */
export const validTLDs: string[] = [
  // Common 2-character TLDs
  "io",
  "co",
  "sa",
  "ae",
  "eg",
  "uk",
  "us",
  "ca",
  "au",
  "de",
  "fr",
  "es",
  "it",
  "jp",
  "cn",
  "in",
  "br",
  "ru",
  "mx",
  "kr",
  "nl",
  "se",
  "pl",
  "tr",
  "be",
  "ch",
  "at",
  "no",
  "dk",
  "fi",
  "il",
  "sg",
  "my",
  "ph",
  "id",
  "vn",
  "th",
  "nz",
  "za",
  // Common 3-character TLDs
  "com",
  "net",
  "org",
  "app",
  "dev",
  "edu",
  "gov",
  "mil",
  "int",
  "biz",
  "pro",
  "tel",
  "web",
  "xyz",
  "tv",
  "fm",
  "am",
  "pm",
];

/**
 * Validates if a URL has a valid TLD extension
 * @param url - The URL string to validate
 * @returns true if the URL has a valid TLD, false otherwise
 */
export const isValidUrlWithTLD = (url: string): boolean => {
  if (!url || url.trim() === "") {
    return false;
  }

  // Use validator package for basic URL structure validation
  if (
    !isURL(url, {
      protocols: ["http", "https"],
      require_protocol: true,
      require_valid_protocol: true,
      require_tld: true,
    })
  ) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Extract the last part after the last dot (the TLD)
    const parts = hostname.split(".");
    const tld = parts[parts.length - 1].toLowerCase();

    // Check if TLD is in the whitelist of valid TLDs
    if (!tld || !validTLDs.includes(tld)) {
      return false;
    }

    // Ensure hostname ends with a dot followed by the TLD
    if (!hostname.match(/\.[a-z]{2,3}$/i)) {
      return false;
    }

    // Additional check: ensure proper domain structure
    // If we have multiple parts, ensure domain name part exists (not just subdomain.tld)
    if (parts.length >= 2) {
      const domainPart = parts[parts.length - 2];
      // Domain name should be at least 2 characters
      if (!domainPart || domainPart.length < 2) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
};
